import { Flex, Icon, IconButton, Input, Td, Text, Tr } from "@chakra-ui/react";
import { FaFileDownload } from "react-icons/fa";
import { PersonDto } from "../../../autogen/bff-api";
import { ProfileImageRounded } from "../../../pages/sourcing-events/buyer/view-all/ProfileImageRounded";
import { useDisplayDate } from "../../formatting/useDisplayDate";
import { DownloadDocumentIconButton } from "../DownloadDocumentIconButton/DownloadDocumentIconButton";
import { ProgressBar } from "./ProgressBar";
import { RemoveFileButton } from "./RemoveFileButton";

interface Props {
  file: FileUpload;
  name: string;
  remove: (file: FileUpload) => Promise<void>;
  onUpdateName?: (name: string) => Promise<void>;
}

export const isIncompleteFileUpload = (upload: FileUpload): upload is IncompleteFileUpload =>
  !!upload.uploadId &&
  (!!upload.name || upload.name === "") &&
  Object.values(IncompleteFileUploadStatus).includes(upload.status as IncompleteFileUploadStatus);

export enum IncompleteFileUploadStatus {
  UploadingToBrowser = "UploadingToBrowser",
  UploadingToServer = "UploadingToServer",
  UploadFailed = "UploadFailed",
}

export type IncompleteFileUpload =
  | {
      status: IncompleteFileUploadStatus.UploadingToBrowser;
      name: string;
      uploadId: string;
    }
  | {
      status: IncompleteFileUploadStatus.UploadingToServer;
      name: string;
      uploadId: string;
    }
  | {
      status: IncompleteFileUploadStatus.UploadFailed;
      name: string;
      uploadId: string;
      rejectedByScan?: boolean;
      registeredOnServer: boolean;
    };

export enum UploadCompletedStatus {
  UploadCompleted = "UploadCompleted",
}

export type FileUpload = IncompleteFileUpload | UploadedFile;

type UploadedFile = {
  status: UploadCompletedStatus.UploadCompleted;
  name: string;
  documentId: string;
  uploadId: string;
  uploadedBy: PersonDto;
  uploadedAt: string;
  fileExtension: string;
  size: number;
};

export const FileRow = ({ file, name, remove, onUpdateName }: Props) => {
  const displayDate = useDisplayDate();
  if (file.status !== UploadCompletedStatus.UploadCompleted)
    return (
      <>
        <Tr>
          <Td borderBottom="none" pb="2">
            <Text fontSize="sm">{name}</Text>
          </Td>
          <Td colSpan={2} borderBottom="none" pb="2"></Td>
          <Td borderBottom="none" pb="2">
            <IconButton aria-label="disabled download icon" icon={<Icon as={FaFileDownload} />} isDisabled />
          </Td>
          <Td borderBottom="none" pb="2">
            <RemoveFileButton
              confirmRemoval={async () => {
                await remove(file);
              }}
            />
          </Td>
        </Tr>
        <Tr>
          <Td borderTop="none" colSpan={5} pt="0">
            <ProgressBar
              status={file.status}
              rejectedByScan={file.status === IncompleteFileUploadStatus.UploadFailed && !!file.rejectedByScan}
            />
          </Td>
        </Tr>
      </>
    );

  return (
    <Tr>
      <Td>
        <Input
          size="sm"
          rounded="md"
          fontWeight={"normal"}
          value={name}
          _focus={{
            borderColor: onUpdateName ? "default" : "inherit",
            boxShadow: onUpdateName ? "default" : "inherit",
          }}
          _disabled={{ color: "smMuted", border: "none" }}
          isDisabled={!onUpdateName}
          onChange={(e) => onUpdateName?.(e.target.value)}
        />
      </Td>
      <Td>
        <Flex alignItems="center">
          <Flex>
            <ProfileImageRounded
              name={file.uploadedBy.firstName ?? undefined}
              backgroundColor={"smPrimary"}
              width={"5"}
              fontSize="xx-small"
            />
          </Flex>
          <Flex pl="1">
            <Text fontSize={"sm"}>{`${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`}</Text>
          </Flex>
        </Flex>
      </Td>
      <Td>
        <Text fontSize={"sm"}>{displayDate(file.uploadedAt)}</Text>
      </Td>
      <Td>
        <DownloadDocumentIconButton documentId={file.documentId} />
      </Td>
      <Td>
        <RemoveFileButton
          confirmRemoval={async () => {
            await remove(file);
          }}
        />
      </Td>
    </Tr>
  );
};
